/**
===================================================
Module: frontend.src.hooks.useNotifications.ts
Description: Custom hook for managing notification state
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import { useState, useCallback } from "react";
import { Notification } from "../components/NotificationSystem";

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback(
    (
      message: string,
      type: "success" | "error" | "info" = "info",
      duration: number = 5000,
    ) => {
      const id =
        Date.now().toString() + Math.random().toString(36).substr(2, 9);
      const notification: Notification = {
        id,
        message,
        type,
        duration,
      };

      setNotifications((prev) => [...prev, notification]);
      return id;
    },
    [],
  );

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id),
    );
  }, []);

  const addSuccess = useCallback(
    (message: string, duration?: number) => {
      return addNotification(message, "success", duration);
    },
    [addNotification],
  );

  const addError = useCallback(
    (message: string, duration?: number) => {
      return addNotification(message, "error", duration || 8000); // Errors stay longer
    },
    [addNotification],
  );

  const addInfo = useCallback(
    (message: string, duration?: number) => {
      return addNotification(message, "info", duration);
    },
    [addNotification],
  );

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    addNotification,
    addSuccess,
    addError,
    addInfo,
    removeNotification,
    clearAll,
  };
};
